/**
 * @architecture-header 1
 * @layer module
 * @domain authentication
 * @responsibility Enter, leave and sign out of a workspace, keeping session and active context separate.
 * @state-owner TanStack Query mutation state; Supabase Auth host adapter
 * @allowed-imports app environment/session; contracts; shared transient reset; TanStack Query; same module query keys
 * @forbidden localStorage; Zustand role ownership; permission inference
 * @public-api auth module
 * @side-effects active-context create and delete requests; Query cache cleanup; host sign-out
 * @decision Session and active context are deliberately decoupled. The session proves identity and
 *   is owned by the host; the active context decides which workspace is open and is a backend
 *   resource. Leaving a workspace deletes only the context, so changing role never costs a
 *   re-authentication — signing in is high-friction, changing role is a routine daily action.
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests workspace Playwright role and sign-out flows
 * @security-boundary authenticated context mutation and session teardown
 * @data-classification membership context and in-memory identity
 * @trust-model backend response is authoritative; frontend selection is not authorization
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type ActiveContext,
  accountRolesResponseSchema,
  type Role,
  roleSchema,
} from '@vispo/contracts';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { resetWorkspaceTransientState } from '../../../shared/lib/transient-reset';
import { authKeys } from './auth.queries';

function contextFromRoleResponse(value: unknown, userId: string): ActiveContext {
  const response = accountRolesResponseSchema.parse(value);
  const role = roleSchema.parse(response.current_role);
  return {
    id: `${userId}:${role}`,
    organizationId: userId,
    classId: null,
    role,
  };
}

export function useSelectActiveContext() {
  const environment = useWorkspaceEnvironment();
  const { session } = useWorkspaceSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (role: Role): Promise<ActiveContext> => {
      if (!session) throw new Error('請先登入再選擇工作區');
      if (environment.demoMode) {
        return {
          id: `${session.userId}:${role}`,
          organizationId: session.userId,
          classId: null,
          role,
        };
      }
      const response = await environment.apiClient.request<unknown>('/api/auth/active-context', {
        method: 'POST',
        body: { role },
      });
      return contextFromRoleResponse(response, session.userId);
    },
    onSuccess: (activeContext) => {
      resetWorkspaceTransientState();
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== authKeys.all[0],
      });
      queryClient.setQueryData(authKeys.activeContext(activeContext.organizationId), activeContext);
    },
  });
}

/**
 * Leaving the workspace, which is **not** signing out.
 *
 * Session and active context are two separate things: the session proves who you are and is owned
 * by the host, while the context decides which workspace you are currently in and is a backend
 * resource. Deleting the context therefore returns the person to the role gate with their sign-in
 * still alive.
 *
 * This is what makes "no in-product role switch" a design rule rather than a punishment. The rule
 * means you may not change role while working — you leave the shell first. It has never meant you
 * must re-authenticate: signing in is high-friction (a password, a code, a Google popup), while
 * changing role is an ordinary thing a cram-school owner does several times a day, assigning a
 * paper as a teacher and then opening it as a student to check how it reads.
 *
 * Everything scoped to the old context is dropped here, so the next role cannot inherit a cache or
 * a half-open dialog from the previous one. Only the auth keys survive, because the session did.
 */
export function useDeleteActiveContext() {
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!environment.demoMode) {
        await environment.apiClient.request('/api/auth/active-context', { method: 'DELETE' });
      }
    },
    onSuccess: () => {
      resetWorkspaceTransientState();
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== authKeys.all[0],
      });
      // Clearing the context query is what drops the shell back to the role gate: WorkspaceShell
      // renders RoleSelection whenever there is no role, so no extra navigation is involved.
      queryClient.setQueriesData({ queryKey: authKeys.all }, null);
    },
  });
}

export function useSignOut() {
  const { signOut } = useWorkspaceSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: signOut,
    onSuccess: () => {
      resetWorkspaceTransientState();
      queryClient.clear();
    },
  });
}
